"use client";
import React, { useEffect, useState } from "react";
import "./DayView.css";
import { HiOutlineTag } from "react-icons/hi2";
import { IoTimeOutline } from "react-icons/io5";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import EditClientModalCalendar from "./EditClientModalCalendar";
import { REST_API_URL } from "../api/config";

const API_BASE = REST_API_URL;

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (code) =>
  CURRENCY_SYMBOL_MAP[code] || code || "";

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

const DayView = ({ token, date, onDateChange, setRefreshKey, onSummaryChange, userCurrency }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState(null);
  const [userCurrencyCode, setUserCurrencyCode] = useState(userCurrency || "USD");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const WORK_DAY_START_HOUR = 8;
  const WORK_DAY_END_HOUR = 23;
  const TOTAL_WORK_HOURS = WORK_DAY_END_HOUR - WORK_DAY_START_HOUR;

  const fetchUserInfo = async () => {
    try {
      if (!token) return;
      
      const cachedData = localStorage.getItem("userInfo");
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const cacheTime = localStorage.getItem("userInfoTimestamp");
        const currentTime = new Date().getTime();
        if (cacheTime && (currentTime - parseInt(cacheTime)) < 3600000) {
          setUserCurrencyCode(parsedData.currency || "USD");
          setUserStatus(parsedData?.user?.status || parsedData?.user_status || "individual");
          return parsedData;
        }
      }

      const headers = buildHeaders(token);
      
      const response = await fetch(`${API_BASE}/auth/userinfo/`, {
        headers,
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const userData = await response.json();
      const status = userData?.user?.status || userData?.user_status || "individual";
      const currency = userData?.currency || "USD";
      
      setUserStatus(status);
      setUserCurrencyCode(currency);
      
      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem("userInfoTimestamp", new Date().getTime().toString());
      
      return userData;
    } catch (err) {
      console.error("⚠️ Failed to fetch user info:", err.message);
      setUserStatus("individual");
      setUserCurrencyCode("USD");
      return null;
    }
  };

  const formatTime = (timeString, format24h = false) => {
    if (!timeString || timeString === "–" || timeString === "Invalid Date") {
      return format24h ? "08:00" : "8:00 AM";
    }

    try {
      if (/^\d{1,2}:\d{2}$/.test(timeString)) {
        const [hours, minutes] = timeString.split(":").map(Number);
        return format24h 
          ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
          : formatAMPM(hours, minutes);
      }

      if (timeString.includes('T') || timeString.includes(' ')) {
        const date = new Date(timeString);
        if (isNaN(date.getTime())) {
          return format24h ? "08:00" : "8:00 AM";
        }
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return format24h
          ? `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
          : formatAMPM(hours, minutes);
      }

      return timeString.length === 5 ? timeString : (format24h ? "08:00" : "8:00 AM");
    } catch (error) {
      console.error("Error formatting time:", error, timeString);
      return format24h ? "08:00" : "8:00 AM";
    }
  };

  const formatAMPM = (hours, minutes) => {
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const mins = minutes.toString().padStart(2, '0');
    return `${hours12}:${mins} ${ampm}`;
  };

  const formatHourLabel = (hour) => {
    if (hour === 24 || hour === 0) return "12:00 AM";
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:00 ${ampm}`;
  };

  const formatTimeWithTimeZone = (timeString, format24h = false) => {
    if (!timeString || timeString === "–" || timeString === "Invalid Date") {
      return format24h ? "08:00" : "8:00 AM";
    }

    try {
      const userTimeZone = getUserTimeZone();
      
      if (/^\d{1,2}:\d{2}$/.test(timeString)) {
        const [hours, minutes] = timeString.split(":").map(Number);
        
        const today = new Date();
        const dateWithTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
        
        const formattedTime = dateWithTime.toLocaleTimeString('en-US', {
          hour: format24h ? '2-digit' : 'numeric',
          minute: '2-digit',
          hour12: !format24h,
          timeZone: userTimeZone
        });
        
        return formattedTime;
      }

      if (timeString.includes('T') || timeString.includes(' ')) {
        const date = new Date(timeString);
        if (isNaN(date.getTime())) {
          return format24h ? "08:00" : "8:00 AM";
        }
        
        const formattedTime = date.toLocaleTimeString('en-US', {
          hour: format24h ? '2-digit' : 'numeric',
          minute: '2-digit',
          hour12: !format24h,
          timeZone: userTimeZone
        });
        
        return formattedTime;
      }

      return format24h ? "08:00" : "8:00 AM";
    } catch (error) {
      console.error("Error formatting time with timezone:", error, timeString);
      return format24h ? "08:00" : "8:00 AM";
    }
  };

  const formatPrice = (price, currency) => {
    const priceNum = parseFloat(price || 0);
    const symbol = getCurrencySymbol(currency || userCurrencyCode);
    
    if (priceNum === 0) return "Free";
    
    return `${symbol}${priceNum.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchAppointments = async (targetDate = date) => {
    if (!token) {
      navigate("/sign-in");
      return;
    }

    setLoading(true);
    try {
      if (!userStatus || !userCurrency) {
        await fetchUserInfo();
      }

      const status = userStatus || "individual";

      const headers = buildHeaders(token);
      
      const res = await axios.post(
        `${API_BASE}/api/client/appointments/by-date/`,
        { date: targetDate },
        { headers }
      );


      const data =
        res.data?.appointments ||
        res.data?.results ||
        res.data?.data ||
        (Array.isArray(res.data) ? res.data : []);

      const validItems = data.filter(
        (item) => item && (item.id || item.client_name || item.worker)
      );

      const detailedEvents = await Promise.all(
        validItems.map(async (item) => {
          try {
            if (item.status?.toLowerCase() === "canceled" || 
                item.status?.toLowerCase() === "cancelled" ||
                item.status?.toLowerCase() === "breake_time") {
              return null;
            }

            let details = {};
            if (item.id) {
              try {
                const detailsRes = await axios.get(
                  `${API_BASE}/client/appointment-details/`,
                  {
                    params: { id: item.id },
                    headers,
                  }
                );
                details = detailsRes.data || {};
              } catch (detailErr) {
                console.warn("Could not fetch appointment details:", detailErr);
              }
            }

            const startTime =
              item.start_time ||
              details.appointment_date_time ||
              item.appointment_date_time;
            const endTime =
              item.end_time ||
              details.appointment_end_time ||
              item.appointment_end_time;

            let computedEnd = endTime;
            if (!computedEnd && details.duration) {
              const dur = Number(details.duration);
              const start = new Date(startTime);
              const end = new Date(start.getTime() + dur * 60000);
              computedEnd = end.toISOString();
            }

            const timeStart24h = formatTimeWithTimeZone(startTime, true);
            const timeEnd24h = formatTimeWithTimeZone(computedEnd, true);
            const timeStartDisplay = formatTimeWithTimeZone(startTime, false);
            const timeEndDisplay = formatTimeWithTimeZone(computedEnd, false);

            const appointmentCurrency = item.currency || details.currency || userCurrencyCode;
            const appointmentCurrencySymbol = getCurrencySymbol(appointmentCurrency);

            return {
              id: item.id,
              name: item.client_name || details.client_name || "Client",
              service: item.service_name || details.service_name || "Service",
              master:
                item.worker?.[0]?.full_name ||
                details.master_name ||
                details.worker_name ||
                (status === "individual" ? "You" : "Master"),
              price: parseFloat(item.price || details.price || 0),
              currency: appointmentCurrency,
              currencySymbol: appointmentCurrencySymbol,
              date: item.date || details.date || targetDate,
              timeStart24h: timeStart24h,
              timeEnd24h: timeEnd24h,
              timeStart: timeStartDisplay,
              timeEnd: timeEndDisplay,
              duration: details.duration || item.duration || 60,
              avatar: item.image || details.client_avatar || item.client_avatar || 
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  item.client_name || details.client_name || "Client"
                )}&background=5563F6&color=fff`,
              worker_avatar: item.worker?.[0]?.image_url || details.worker_avatar || 
                "https://ui-avatars.com/api/?name=Master&background=6c5ce7&color=fff",
              status: item.status || details.status || "Scheduled",
            };
          } catch (error) {
            console.error("Error processing appointment:", error);
            return null;
          }
        })
      );

      const filteredEvents = detailedEvents.filter(Boolean);
      
      filteredEvents.sort((a, b) => {
        const [aHour, aMin] = a.timeStart24h.split(":").map(Number);
        const [bHour, bMin] = b.timeStart24h.split(":").map(Number);
        return (aHour * 60 + aMin) - (bHour * 60 + bMin);
      });

      setEvents(filteredEvents);

      let total = 0;
      let dayCurrency = userCurrencyCode || "USD";
      
      filteredEvents.forEach((ev) => {
        total += parseFloat(ev.price || 0);
        if (ev.price > 0 && ev.currency && !dayCurrency) {
          dayCurrency = ev.currency;
        }
      });

      const currencySymbol = getCurrencySymbol(dayCurrency);
      
      if (onSummaryChange) {
        onSummaryChange({ 
          price: total, 
          currency: dayCurrency,
          currencySymbol: currencySymbol,
        });
      }

    } catch (err) {
      console.error("❌ Fetch Error:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments(date);
  }, [date]);

  useEffect(() => {
    if (userCurrency) {
      setUserCurrencyCode(userCurrency);
    }
  }, [userCurrency]);

  const hours = Array.from({ length: TOTAL_WORK_HOURS + 1 }, (_, i) => WORK_DAY_START_HOUR + i);

  const getTopPosition = (time24h) => {
    try {
      if (!time24h || !time24h.includes(":")) return 0;
      const [hour, minute] = time24h.split(":").map(Number);
      
      if (hour < WORK_DAY_START_HOUR || hour > WORK_DAY_END_HOUR) {
        console.warn(`Time ${time24h} is outside working hours (${WORK_DAY_START_HOUR}-${WORK_DAY_END_HOUR})`);
        return hour < WORK_DAY_START_HOUR ? 0 : (WORK_DAY_END_HOUR - WORK_DAY_START_HOUR) * 120;
      }
      
      const totalMinutes = (hour - WORK_DAY_START_HOUR) * 60 + minute;
      return totalMinutes * 2;
    } catch (error) {
      console.error("Error calculating top position:", error, time24h);
      return 0;
    }
  };

  const getHeight = (start24h, end24h) => {
    try {
      const top = getTopPosition(start24h);
      const bottom = getTopPosition(end24h);
      const height = Math.max(bottom - top, 60);
      
      return Math.min(height, 600);
    } catch (error) {
      console.error("Error calculating height:", error);
      return 120;
    }
  };

  const getEventGroup = (event) => {
    const eventTime = getTopPosition(event.timeStart24h);
    return events.filter(e => {
      const otherTime = getTopPosition(e.timeStart24h);
      return Math.abs(otherTime - eventTime) < 30;
    });
  };

  const getLeftOffset = (event) => {
    const group = getEventGroup(event);
    const index = group.findIndex(e => e.id === event.id);
    return 60 + (index * 280);
  };

  const getWidth = (event) => {
    const group = getEventGroup(event);
    return group.length > 1 ? 260 : 280;
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "doing":
      case "in progress":
        return "status-badge doing";
      case "canceled":
      case "cancelled":
        return "status-badge canceled";
      case "late":
        return "status-badge late";
      case "completed":
      case "done":
        return "status-badge completed";
      default:
        return "status-badge";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "doing":
      case "in progress":
        return "DOING";
      case "canceled":
      case "cancelled":
        return "CANCELED";
      case "late":
        return "LATE";
      case "completed":
      case "done":
        return "COMPLETED";
      default:
        return "UPCOMING";
    }
  };

  const openModal = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setIsModalOpen(false);
  };

  const handleSaveSuccess = () => {
    fetchAppointments(date);
    setRefreshKey && setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="loading flex flex-col items-center justify-center h-[400px]"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
          className="w-12 h-12 border-4 border-[#5563F6] border-t-transparent rounded-full mb-3"
        />
        <p className="text-[#5563F6] text-sm font-medium">
          Loading appointments...
        </p>
      </motion.div>
    );
  }

  if (!events.length) {
    return (
      <div className="empty-day flex flex-col items-center justify-center h-[400px] text-center">
        <div className="text-4xl mb-3 text-gray-300">📅</div>
        <p className="text-[#9A99AB] text-base font-medium mb-2">
          No appointments for this day
        </p>
        <p className="text-sm text-gray-400">
          Click "Add Appointment" to schedule a new appointment
        </p>
      </div>
    );
  }

  const eventStartHours = events.map(e => {
    const hour = parseInt(e.timeStart24h.split(":")[0]);
    return Math.max(WORK_DAY_START_HOUR, Math.min(hour, WORK_DAY_END_HOUR));
  });
  
  const eventEndHours = events.map(e => {
    const hour = parseInt(e.timeEnd24h.split(":")[0]) + 1; // +1 для отступа
    return Math.max(WORK_DAY_START_HOUR + 1, Math.min(hour, WORK_DAY_END_HOUR));
  });
  
  const minHour = Math.min(...eventStartHours, WORK_DAY_START_HOUR);
  const maxHour = Math.max(...eventEndHours, WORK_DAY_END_HOUR - 2);
  const visibleHours = hours.filter(h => h >= minHour && h <= maxHour);

  return (
    <div className="dayview-container">
      <div className="dayview-grid">
        {visibleHours.map((hour) => (
          <div className="hour-row" key={hour}>
            <div className="hour-label">{formatHourLabel(hour)}</div>
            <div className="hour-line" />
            <div className="dot" />
          </div>
        ))}

        {events.map((event) => {
          const top = getTopPosition(event.timeStart24h);
          const height = getHeight(event.timeStart24h, event.timeEnd24h);
          const left = getLeftOffset(event);
          const width = getWidth(event);

          if (top < 0 || top > (TOTAL_WORK_HOURS * 120) || height < 30) {
            console.warn(`Skipping event with invalid position:`, event);
            return null;
          }

          return (
            <motion.div
              key={event.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="appointment-card appointment-card-day-view"
              style={{
                top: `${top}px`,
                left: `${left}px`,
                height: `${height}px`,
                width: `${width}px`,
              }}
              onClick={() => openModal(event)}
            >
              <div className="appointment-header">
                <img 
                  src={event.avatar} 
                  alt="client" 
                  className="avatar"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(event.name)}&background=5563F6&color=fff`;
                  }}
                />
                <div className="client-info">
                  <div className="client-name">{event.name}</div>
                  <div className="service-row">
                    <HiOutlineTag className="inline mr-1" />
                    <span className="truncate">
                      {event.service} &nbsp; 
                      {formatPrice(event.price, event.currency)}
                    </span>
                  </div>
                </div>
                <div
                  className="menu-dots"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(event);
                  }}
                >
                  ⋯
                </div>
              </div>

              <div className="time-row sleek">
                <div className="time-text">
                  <IoTimeOutline className="inline mr-1" />
                  {event.timeStart} – {event.timeEnd}
                </div>
                <div className={getStatusClass(event.status)}>
                  {getStatusText(event.status)}
                </div>
              </div>

              {height > 100 && (
                <div className="duration-info">
                  <div className="flex items-center">
                    <IoTimeOutline className="shrink-0 mr-1" />
                    <span>Duration:</span>
                  </div>
                  <span className="duration-minutes">{event.duration} min</span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {isModalOpen && selectedEvent && (
        <EditClientModalCalendar
          isOpen={isModalOpen}
          event={selectedEvent}
          onClose={closeModal}
          onSaveSuccess={handleSaveSuccess}
          userCurrency={userCurrencyCode}
        />
      )}
    </div>
  );
};

export default DayView;